import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/ru';
import { DateFormat, TimeFormat } from '../types/common/date-types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.locale('ru');

const MOSCOW_TIMEZONE = 'Europe/Moscow';

const datesUtil = {
  getMonthStart: (date: Date): Date => {
    return dayjs(date).startOf('month').toDate();
  },
  getMonthEnd: (date: Date): Date => {
    return dayjs(date).endOf('month').toDate();
  },
  formatDate: (date: Date, format: DateFormat): DateString => {
    return dayjs(date).format(format) as DateString;
  },
  formatDateFromString: (dateString: DateString, format: DateFormat): DateString => {
    return dayjs(dateString).format(format) as DateString;
  },
  addMinutes: (date: Date, minutes: number): Date => {
    return dayjs(date).add(minutes, 'minute').toDate();
  },
  addHours: (date: Date, hours: number): Date => {
    return dayjs(date).add(hours, 'hour').toDate();
  },
  addDays: (date: Date, days: number): Date => {
    return dayjs(date).add(days, 'day').toDate();
  },
  addDaysToCalendarDate: (dateString: DateString, days: number, format: DateFormat): DateString => {
    return dayjs(dateString.slice(0, 10)).add(days, 'day').format(format) as DateString;
  },
  getTimestamp: (date: DateString): Timestamp => {
    return dayjs(date).valueOf() as Timestamp;
  },
  subtractDays: (date: Date, days: number): Date => {
    return dayjs(date).subtract(days, 'day').toDate();
  },
  diffDays: (dateFrom: Date, dateTo: DateString): NumberOfDays => {
    return Math.ceil(dayjs(dateTo).diff(dateFrom, 'days', true)) as NumberOfDays;
  },
  minutesToFormattedTime: (minutes: NumberOfMinutes, format: TimeFormat): FormattedTime => {
    return dayjs.duration(minutes, 'minutes').format(format) as FormattedTime;
  },
  secondsToFormattedTime: (seconds: NumberOfSeconds, format: TimeFormat): FormattedTime => {
    return dayjs.duration(seconds, 'seconds').format(format) as FormattedTime;
  },
  formattedTimeToMinutes: (time: FormattedTime, format: TimeFormat): NumberOfMinutes => {
    return (dayjs(time, format).hour() * 60 + dayjs(time, format).minute()) as NumberOfMinutes;
  },
  secondsToMinutesSeconds(totalSeconds: NumberOfSeconds) {
    const d = dayjs.duration(totalSeconds || 0, 'seconds');

    const minutes = Math.floor(d.asMinutes());
    const seconds = d.seconds();

    return [minutes, seconds];
  },
  differenceInSecondsFromCurrentTime: (estimatedTime: Date): NumberOfSeconds => {
    const differentTime = Math.abs(estimatedTime.getTime() - new Date().getTime());

    return Number((differentTime / 1000).toFixed()) as NumberOfSeconds;
  },
  isBetweenDay: (dateOne: DateString | Date, dateTwo: DateString | Date): boolean => {
    return dayjs().isBetween(dateOne, dateTwo, 'day');
  },
  getPreviousMonthString: (): MonthName => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let previousMonth = currentMonth - 1;
    let previousYear = currentYear;

    if (previousMonth < 0) {
      previousMonth = 11;
      previousYear -= 1;
    }

    const previousMonthDate = new Date(previousYear, previousMonth, 1);
    const stringMonth = previousMonthDate.toLocaleString('ru-RU', { month: 'long' });
    return (stringMonth[0].toUpperCase() + stringMonth.slice(1)) as MonthName;
  },
  secondsFromMinutes: (timeToFormat: FormattedTime): NumberOfSeconds | undefined => {
    if (!timeToFormat) {
      return;
    }

    const timeArray = timeToFormat.split(':');

    const minutes = parseInt(timeArray[0]);
    const seconds = parseInt(timeArray[1]);

    return (minutes * 60 + seconds) as NumberOfSeconds;
  },
  formatDayStartToISO: (date: Date): DateString => {
    return dayjs(date).startOf('day').toISOString() as DateString;
  },
  formatDayEndToISO: (date: Date): DateString => {
    return dayjs(date).endOf('day').toISOString() as DateString;
  },
  getMoscowDate: (): Date => {
    return dayjs().tz(MOSCOW_TIMEZONE).toDate();
  },
  getMinutesFromMoscowDayStart: (): NumberOfMinutes => {
    const moscowTime = dayjs().tz(MOSCOW_TIMEZONE);

    const hours = moscowTime.hour();
    const minutes = moscowTime.minute();

    return (hours * 60 + minutes) as NumberOfMinutes;
  },
};

export { datesUtil };
